import { CheckCircle2, Loader2, XCircle, Circle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/utils/styles';

type ProcessingStatus = 'completed' | 'processing' | 'failed' | 'not started';

interface ImageItem {
  name: string;
  status: ProcessingStatus;
}

interface ProcessingStatusBannerProps {
  images: ImageItem[];
}

const getStatusConfig = (t: (key: string) => string) => ({
  completed: {
    icon: CheckCircle2,
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-50 dark:bg-emerald-950/30',
    label: t('insights-status-completed'),
  },
  processing: {
    icon: Loader2,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-950/30',
    label: t('insights-status-processing'),
  },
  failed: {
    icon: XCircle,
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-950/30',
    label: t('insights-status-failed'),
  },
  'not started': {
    icon: Circle,
    color: 'text-muted-foreground',
    bgColor: 'bg-muted',
    label: t('insights-status-not-started'),
  },
});

export function ProcessingStatusBanner({ images }: ProcessingStatusBannerProps) {
  const { t } = useTranslation();
  const statusConfig = getStatusConfig(t);

  const completedCount = images.filter((img) => img.status === 'completed').length;
  const totalCount = images.length;
  const processingCount = images.filter((img) => img.status === 'processing').length;
  const failedCount = images.filter((img) => img.status === 'failed').length;

  return (
    <div className="space-y-4 rounded-lg border bg-card p-4 text-card-foreground">
      {/* Header with progress summary */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-pretty">{t('insights-processing-title')}</h3>
          <p className="text-sm text-muted-foreground">
            {t('insights-processing-images-processed', {
              completed: completedCount,
              total: totalCount,
            })}
          </p>
        </div>
        <div className="flex gap-2 text-sm">
          {processingCount > 0 && (
            <span className="rounded-full bg-blue-100 px-2.5 py-0.5 font-medium text-blue-700 dark:bg-blue-950/50 dark:text-blue-400">
              {t('insights-processing-count', { count: processingCount })}
            </span>
          )}
          {failedCount > 0 && (
            <span className="rounded-full bg-red-100 px-2.5 py-0.5 font-medium text-red-700 dark:bg-red-950/50 dark:text-red-400">
              {t('insights-failed-count', { count: failedCount })}
            </span>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full bg-emerald-600 transition-all duration-500 dark:bg-emerald-400"
          style={{ width: `${(completedCount / totalCount) * 100}%` }}
        />
      </div>

      {/* Image list */}
      <div className="max-h-48 space-y-2 overflow-y-auto">
        {images.map((image, index) => {
          const config = statusConfig[image.status];
          const Icon = config.icon;

          return (
            <div
              key={index}
              className={cn(
                'flex items-center gap-3 rounded-md p-2.5 transition-colors',
                config.bgColor
              )}
            >
              <Icon
                className={cn(
                  'h-4 w-4 shrink-0',
                  config.color,
                  image.status === 'processing' && 'animate-spin'
                )}
              />
              <span className="flex-1 truncate text-sm font-medium">{image.name}</span>
              <span className={cn('text-xs font-medium', config.color)}>{config.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
